import { useMemo, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DialogContext,
  type ConfirmOptions,
  type DialogApi,
  type PromptOptions,
} from "@/lib/confirm";
import { useT } from "@/lib/i18n";

type Pending =
  | { kind: "confirm"; options: ConfirmOptions; resolve: (ok: boolean) => void }
  | { kind: "prompt"; options: PromptOptions; resolve: (value: string | null) => void };

const PROMPT_FORM_ID = "tn-prompt-form";
const PROMPT_INPUT_ID = "tn-prompt-input";

/** `useConfirm` / `usePrompt` için tek ortak diyalog. Aynı anda tek istek. */
export function DialogProvider({ children }: { children: ReactNode }) {
  const t = useT("common");
  const [pending, setPending] = useState<Pending | null>(null);
  const [value, setValue] = useState("");

  const api = useMemo<DialogApi>(
    () => ({
      confirm: (options) =>
        new Promise<boolean>((resolve) => setPending({ kind: "confirm", options, resolve })),
      prompt: (options) =>
        new Promise<string | null>((resolve) => {
          setValue(options.defaultValue ?? "");
          setPending({ kind: "prompt", options, resolve });
        }),
    }),
    [],
  );

  function settle(accepted: boolean) {
    if (!pending) return;
    if (pending.kind === "confirm") pending.resolve(accepted);
    else pending.resolve(accepted ? value : null);
    setPending(null);
  }

  const destructive = pending?.kind === "confirm" && pending.options.destructive === true;

  return (
    <DialogContext.Provider value={api}>
      {children}
      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        {pending ? (
          <DialogContent>
            <DialogHeader
              title={pending.options.title}
              description={pending.options.description}
            />
            {pending.kind === "prompt" ? (
              <form
                id={PROMPT_FORM_ID}
                className="space-y-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  settle(true);
                }}
              >
                {pending.options.label ? (
                  <Label htmlFor={PROMPT_INPUT_ID}>{pending.options.label}</Label>
                ) : null}
                <Input
                  id={PROMPT_INPUT_ID}
                  autoFocus
                  inputMode={pending.options.inputMode ?? "text"}
                  value={value}
                  placeholder={pending.options.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                />
              </form>
            ) : null}
            <DialogFooter>
              <Button variant="outline" autoFocus={destructive} onClick={() => settle(false)}>
                {pending.options.cancelLabel ?? t("cancel")}
              </Button>
              {pending.kind === "prompt" ? (
                <Button type="submit" form={PROMPT_FORM_ID}>
                  {pending.options.confirmLabel ?? t("save")}
                </Button>
              ) : (
                <Button
                  variant={destructive ? "destructive" : "default"}
                  autoFocus={!destructive}
                  onClick={() => settle(true)}
                >
                  {pending.options.confirmLabel ?? t("confirm")}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        ) : null}
      </Dialog>
    </DialogContext.Provider>
  );
}
